import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Sparkles, Eye, MapPin, Navigation } from "lucide-react";
import { API, api } from "@/lib/api";

const DEFAULT_BG =
  "https://static.prod-images.emergentagent.com/jobs/1c4c9bcc-15eb-4aff-af44-10f350d3e934/images/6eeb55e1b5ec1ea46c8a72c3504e628cbbbea24669e3818d9450f7765c663782.png";

function useTheme(theme) {
  return useMemo(() => {
    const accent = theme?.accent || "#8B9D83";
    const mode = theme?.background || "dark";
    const isLight = mode === "light";
    const fontClass = theme?.font === "serif"
      ? "font-serif"
      : theme?.font === "mono"
      ? "font-mono"
      : theme?.font === "cinzel"
      ? "font-cinzel"
      : theme?.font === "poppins"
      ? "font-poppins"
      : theme?.font === "helvetica"
      ? "font-helvetica"
      : "font-display";
    const bgImage = theme?.bg_image_path ? `${API}/files/${theme.bg_image_path}` : null;
    const currency = theme?.currency || "EUR";
    const sym = { EUR: "€", USD: "$", GBP: "£", INR: "₹", JPY: "¥" }[currency] || "€";
    const fmt = (n) => `${sym}${Number(n || 0).toFixed(2)}`;
    const showPrice = theme?.show_price !== false;  // default true; hide only when explicitly disabled
    return { accent, mode, isLight, fontClass, bgImage, currency, sym, fmt, showPrice };
  }, [theme]);
}

export default function Kiosk() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const isPreview = search.get("preview") === "1";
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | quiz | loading | reveal
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Preview links are token-scoped and PUBLIC — anyone with the link can view.
    // Use axios (no auth header) so it works in any browser / shared tab.
    const fetcher = isPreview
      ? axios.get(`${API}/public/experience/${token}/preview`)
      : axios.get(`${API}/public/experience/${token}`);
    fetcher
      .then((r) => setConfig(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Experience not found"));
  }, [token, isPreview]);

  const theme = useTheme(config?.theme);

  const start = () => {
    setAnswers({});
    setStep(0);
    setResult(null);
    setPhase("quiz");
  };
  const restart = () => setPhase("idle");

  const submit = async (finalAnswers) => {
    setPhase("loading");
    const payload = {
      answers: (config?.questions || []).map((q) => ({
        question_id: q.id,
        question_text: q.text,
        selected: finalAnswers[q.id]?.selected || [],
      })),
    };
    try {
      const reqFn = isPreview
        ? axios.post(`${API}/public/experience/${token}/preview/recommend`, payload)
        : axios.post(`${API}/public/experience/${token}/recommend`, payload);
      const { data } = await reqFn;
      setResult(data);
      setPhase("reveal");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not generate recommendation");
      setPhase("idle");
    }
  };

  const pickOption = (q, opt) => {
    const next = { ...answers, [q.id]: { selected: [{ label: opt.label, tags: opt.tags || [] }] } };
    setAnswers(next);
    const isLast = step >= (config.questions.length - 1);
    if (isLast) submit(next);
    else setTimeout(() => setStep(step + 1), 220);
  };

  if (error) {
    return (
      <div className="kiosk-root flex items-center justify-center min-h-screen p-12 text-center">
        <div>
          <div className="label-overline text-white/50">Error</div>
          <h1 className="mt-4 font-display font-black tracking-tighter text-4xl">{error}</h1>
        </div>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="kiosk-root flex items-center justify-center min-h-screen text-white/60">
        <div className="label-overline">Loading experience…</div>
      </div>
    );
  }

  const hasQuestions = (config.questions || []).length > 0;
  const rootBg = theme.isLight ? "#fafafa" : "#0a0a0a";
  const rootFg = theme.isLight ? "#111" : "#fff";

  return (
    <div
      className="relative overflow-hidden min-h-screen"
      style={{ background: rootBg, color: rootFg, fontFamily: "IBM Plex Sans, system-ui, sans-serif" }}
    >
      {isPreview && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-1.5 bg-amber-400 text-black font-mono text-[10px] uppercase tracking-[0.25em]"
          data-testid="preview-badge"
        >
          <Eye className="w-3 h-3" /> Preview mode · not visible to public
        </div>
      )}
      <AnimatePresence mode="wait">
        {phase === "idle" && (
          <IdleScreen
            key="idle"
            config={config}
            theme={theme}
            hasQuestions={hasQuestions}
            onStart={start}
          />
        )}

        {phase === "quiz" && hasQuestions && (
          <QuizScreen
            key={`q-${step}`}
            config={config}
            theme={theme}
            step={step}
            onPick={pickOption}
            answers={answers}
          />
        )}

        {phase === "loading" && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center"
            data-testid="kiosk-loading"
          >
            <div className="text-center">
              <Sparkles className="w-10 h-10 mx-auto animate-pulse" style={{ color: theme.accent }} />
              <div className="label-overline mt-6 opacity-70">Curating your match</div>
              <div className={`mt-2 font-display font-bold text-3xl tracking-tight`}>One moment…</div>
            </div>
          </motion.div>
        )}

        {phase === "reveal" && result && (
          <RevealScreen key="reveal" brand={config.brand} theme={theme} result={result} onRestart={restart} />
        )}
      </AnimatePresence>
    </div>
  );
}

function IdleScreen({ config, theme, hasQuestions, onStart }) {
  const bgUrl = theme.bgImage || (theme.mode === "image" ? null : DEFAULT_BG);
  const showBgImage = theme.mode === "image" ? !!theme.bgImage : (theme.mode === "dark");
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      onClick={hasQuestions ? onStart : undefined}
      className="relative min-h-screen flex flex-col justify-between p-12 cursor-pointer"
      data-testid="kiosk-idle"
    >
      {showBgImage && bgUrl && (
        <img src={bgUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
      )}
      {showBgImage && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/80" />
      )}
      <div className="relative">
        {config.theme?.logo_path ? (
          <img src={`${API}/files/${config.theme.logo_path}`} alt="" className="h-16 lg:h-20 w-auto" data-testid="kiosk-idle-logo" />
        ) : (
          <div className="label-overline opacity-70" data-testid="kiosk-idle-brandname">{config.brand.name || "FORME"}</div>
        )}
      </div>
      <div className="relative">
        <h1 className={`${theme.fontClass} font-black tracking-tighter text-7xl lg:text-9xl leading-[0.85] max-w-5xl`}>
          {config.theme?.headline || "Find your perfect piece."}
        </h1>
        <p className="mt-8 opacity-70 text-base lg:text-lg max-w-lg">
          {config.brand.tagline || `A quick journey. A tailored result. Curated by ${config.brand.name || "us"}.`}
        </p>
        <div className="mt-12 inline-flex items-center gap-4 animate-slow-pulse">
          <div className="w-3 h-3" style={{ background: theme.accent }} />
          <span className="label-overline" style={{ color: "inherit" }}>
            {hasQuestions ? "Tap anywhere to begin" : "This brand hasn't published questions yet"}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function QuizScreen({ config, theme, step, onPick, answers }) {
  const q = config.questions[step];
  const progress = ((step + 1) / config.questions.length) * 100;
  const optsHaveImages = (q.options || []).some((o) => o.image_path);
  const heroImg = q.image_path ? `${API}/files/${q.image_path}` : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="min-h-screen flex flex-col p-10 lg:p-14"
      data-testid={`kiosk-question-${step}`}
    >
      <div className="flex items-center justify-between">
        <div className="label-overline opacity-70">
          {config.brand.name} · Question {step + 1} of {config.questions.length}
        </div>
        <div className="font-mono text-xs opacity-70">
          {String(step + 1).padStart(2, "0")} / {String(config.questions.length).padStart(2, "0")}
        </div>
      </div>
      <div className="mt-3 w-full bg-white/10 h-px relative">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.45 }}
          className="absolute left-0 top-0 h-px"
          style={{ background: theme.accent }}
        />
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full">
        {heroImg && (
          <div className="mb-8 max-h-48 overflow-hidden border border-white/10 bg-black/20">
            <img
              src={heroImg}
              alt=""
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-full h-48 object-cover"
            />
          </div>
        )}
        <h2
          className={`${theme.fontClass} font-black tracking-tighter text-4xl lg:text-6xl leading-[0.95]`}
          data-testid="kiosk-question-text"
        >
          {q.text}
        </h2>

        <div className={`mt-12 grid gap-5 ${optsHaveImages ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2"}`}>
          {(q.options || []).map((opt, i) => {
            const selected = answers[q.id]?.selected?.[0]?.label === opt.label;
            const hasImg = !!opt.image_path;
            return (
              <motion.button
                key={i}
                onClick={() => onPick(q, opt)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                className={`kiosk-glass ${selected ? "active" : ""} text-left overflow-hidden flex flex-col`}
                data-testid={`kiosk-option-${i}`}
                style={{ borderColor: selected ? theme.accent : undefined }}
              >
                {hasImg && (
                  <div className="aspect-[4/3] overflow-hidden bg-black/20">
                    <img
                      src={`${API}/files/${opt.image_path}`}
                      alt=""
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = "0"; }}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-6 flex items-center justify-between flex-1">
                  <span className={`${theme.fontClass} font-bold tracking-tight ${hasImg ? "text-xl lg:text-2xl" : "text-2xl lg:text-3xl"}`}>
                    {opt.label}
                  </span>
                  <ArrowRight className="w-5 h-5 opacity-40 shrink-0 ml-3" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

function ProductImage({ p, className = "" }) {
  const [errored, setErrored] = useState(false);
  const fallback = "https://images.pexels.com/photos/18155790/pexels-photo-18155790.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900";
  const src = !errored && p.image_path ? `${API}/files/${p.image_path}` : (errored ? fallback : (p.image_path ? `${API}/files/${p.image_path}` : fallback));
  return (
    <img
      src={src}
      alt={p.title}
      loading="lazy"
      onError={() => setErrored(true)}
      className={className}
    />
  );
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function fmtDistance(km) {
  if (km < 1) return `${Math.round(km * 1000)} m away`;
  return `${km.toFixed(1)} km away`;
}

function AvailabilityBlock({ locations, theme }) {
  const [geo, setGeo] = useState(null); // null (pending) | {lat,lng} | "denied"

  useEffect(() => {
    if (!locations?.length) return;
    if (!navigator.geolocation) {
      setGeo("denied");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setGeo("denied"),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }, [locations]);

  if (!locations?.length) return null;

  let sorted = locations;
  const hasCoords = geo && geo !== "denied";
  if (hasCoords) {
    sorted = locations
      .map((l) => ({
        ...l,
        _d: l.lat != null && l.lng != null ? haversineKm(geo.lat, geo.lng, l.lat, l.lng) : null,
      }))
      .sort((a, b) => (a._d ?? Infinity) - (b._d ?? Infinity));
  }
  const nearest = sorted[0];
  const others = sorted.slice(1, 3);
  const showDistance = hasCoords && nearest._d != null;

  const borderCol = theme.isLight ? "#e5e5e5" : "rgba(255,255,255,0.15)";

  return (
    <div className="mt-8 max-w-md" data-testid="kiosk-availability">
      <div className="label-overline opacity-70 flex items-center gap-2">
        <MapPin className="w-3.5 h-3.5" style={{ color: theme.accent }} />
        {showDistance ? "Available closest to you" : "Available at"}
      </div>
      <div className="mt-3 border p-4" style={{ borderColor: theme.accent }} data-testid="kiosk-nearest-store">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={`${theme.fontClass} font-bold tracking-tight text-xl`}>{nearest.name}</div>
            {nearest.address && <div className="mt-1 text-sm opacity-70">{nearest.address}</div>}
          </div>
          {showDistance && (
            <div
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono"
              style={{ background: theme.accent, color: "#fff" }}
              data-testid="kiosk-store-distance"
            >
              <Navigation className="w-3 h-3" /> {fmtDistance(nearest._d)}
            </div>
          )}
        </div>
      </div>
      {others.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="kiosk-other-stores">
          {others.map((l) => (
            <div
              key={l.id}
              className="flex items-center justify-between gap-3 border px-4 py-2 text-sm opacity-70"
              style={{ borderColor: borderCol }}
            >
              <span className="truncate">
                <span className="font-medium">{l.name}</span>
                {l.address && <span> · {l.address}</span>}
              </span>
              {hasCoords && l._d != null && (
                <span className="font-mono text-xs shrink-0">{fmtDistance(l._d)}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {geo === null && (
        <p className="mt-2 text-xs opacity-50" data-testid="kiosk-geo-pending">
          Allow location access to see the store closest to you.
        </p>
      )}
    </div>
  );
}


function RevealScreen({ brand, theme, result, onRestart }) {
  const ctaLabel = result?.cta_label || (window?.location && null) || "I want this";
  const top = result.top || [];
  const primary = top[0];
  const others = top.slice(1, 3);
  const comp = result.complementary;

  if (!primary) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen"
      data-testid="kiosk-reveal"
    >
      {/* Hero match */}
      <div className="grid lg:grid-cols-2 lg:min-h-[80vh]">
        <motion.div
          initial={{ scale: 1.04, filter: "blur(12px)" }}
          animate={{ scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-[#111] overflow-hidden h-[55vh] min-h-[320px] lg:h-auto lg:min-h-0"
          data-testid="kiosk-reveal-hero"
        >
          <motion.div
            initial={{ scale: 1.0 }}
            animate={{ scale: 1.08 }}
            transition={{ duration: 14, ease: "linear" }}
            className="absolute inset-0"
          >
            <ProductImage p={primary.product} className="absolute inset-0 w-full h-full object-cover" />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-tr from-black/65 via-black/10 to-transparent" />
          <div className="absolute top-12 left-12 label-overline text-white/70">{brand.name}</div>
          <div
            className="absolute bottom-12 left-12 px-3 py-1 text-xs font-mono"
            style={{ background: theme.accent, color: "#fff" }}
          >
            BEST MATCH · {primary.match_score}/100
          </div>
        </motion.div>

        <div className="flex flex-col justify-center p-6 sm:p-10 lg:p-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
          >
            <div className="label-overline opacity-70">Your top pick</div>
            <h2 className={`${theme.fontClass} font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mt-4 break-words`} data-testid="kiosk-reveal-title">
              {primary.product.title}
            </h2>
            {theme.showPrice && (
              <div className="mt-3 font-display font-bold text-2xl tracking-tight opacity-80" data-testid="kiosk-reveal-price">
                {theme.fmt(primary.product.price)}
              </div>
            )}
            <p className="mt-6 max-w-md opacity-70 leading-relaxed">{primary.reason}</p>

            <AvailabilityBlock locations={primary.product.locations} theme={theme} />

            <div className="mt-10 flex flex-wrap gap-4">
              {primary.product.product_url ? (
                <a
                  href={primary.product.product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-7 h-12 font-medium tracking-tight inline-flex items-center"
                  style={{ background: theme.accent, color: "#fff" }}
                  data-testid="kiosk-reveal-cta"
                >
                  {ctaLabel}
                </a>
              ) : null}
              <button
                onClick={onRestart}
                className="border border-white/30 hover:border-white opacity-80 hover:opacity-100 px-7 h-12 flex items-center gap-2"
                style={{ borderColor: theme.isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.3)" }}
                data-testid="kiosk-restart"
              >
                <RotateCcw className="w-4 h-4" /> Start over
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Other matches + complementary */}
      {(others.length > 0 || comp) && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="border-t px-6 sm:px-10 lg:px-16 py-10 lg:py-12"
          style={{ borderColor: theme.isLight ? "#e5e5e5" : "rgba(255,255,255,0.1)" }}
          data-testid="kiosk-other-matches"
        >
          <div className="grid lg:grid-cols-3 gap-px" style={{ background: theme.isLight ? "#e5e5e5" : "rgba(255,255,255,0.08)" }}>
            {others.map((m, i) => (
              <div
                key={m.product.id}
                className="p-6 flex flex-col"
                style={{ background: theme.isLight ? "#fafafa" : "#0a0a0a" }}
                data-testid={`kiosk-alt-${i}`}
              >
                <div className="label-overline opacity-70">Also for you · #{i + 2}</div>
                <div className="aspect-[4/3] mt-4 overflow-hidden border" style={{ borderColor: theme.isLight ? "#e5e5e5" : "rgba(255,255,255,0.1)" }}>
                  <ProductImage p={m.product} className="w-full h-full object-cover" />
                </div>
                <div className={`${theme.fontClass} font-bold text-2xl tracking-tight mt-4`}>{m.product.title}</div>
                {theme.showPrice && (
                  <div className="font-mono text-sm opacity-70 mt-1" data-testid={`kiosk-alt-price-${i}`}>{theme.fmt(m.product.price)}</div>
                )}
                <p className="mt-3 text-sm opacity-70 line-clamp-3">{m.reason}</p>
                {m.product.product_url && (
                  <a
                    href={m.product.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex self-start items-center h-10 px-5 text-sm font-medium tracking-tight"
                    style={{ background: theme.accent, color: "#fff" }}
                    data-testid={`kiosk-alt-cta-${i}`}
                  >
                    {ctaLabel}
                  </a>
                )}
              </div>
            ))}
            {comp && (
              <div
                className="p-6 relative flex flex-col"
                style={{ background: theme.isLight ? "#fafafa" : "#0a0a0a" }}
                data-testid="kiosk-complementary"
              >
                <div className="label-overline" style={{ color: theme.accent }}>
                  ✦ Pairs well with your pick
                </div>
                <div className="aspect-[4/3] mt-4 overflow-hidden border" style={{ borderColor: theme.accent }}>
                  <ProductImage p={comp.product} className="w-full h-full object-cover" />
                </div>
                <div className={`${theme.fontClass} font-bold text-2xl tracking-tight mt-4`}>{comp.product.title}</div>
                {theme.showPrice && (
                  <div className="font-mono text-sm opacity-70 mt-1" data-testid="kiosk-complementary-price">{theme.fmt(comp.product.price)}</div>
                )}
                <p className="mt-3 text-sm opacity-70">{comp.reason}</p>
                {comp.product.product_url && (
                  <a
                    href={comp.product.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex self-start items-center h-10 px-5 text-sm font-medium tracking-tight"
                    style={{ background: theme.accent, color: "#fff" }}
                    data-testid="kiosk-complementary-cta"
                  >
                    {ctaLabel}
                  </a>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
